import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  Bell, Gavel, ReceiptText, MessageSquare, Package, Wallet, Star, CheckCheck, Loader2, ShieldCheck, ShieldAlert, Building2,
} from "lucide-react";
import { api } from "../lib/api";

const ICONS: Record<string, any> = {
  bid_received: Gavel, bid_shortlisted: Gavel, bid_awarded: Gavel, bid_negotiation: Gavel,
  quote_received: ReceiptText, quote_shortlisted: ReceiptText, quote_awarded: ReceiptText,
  message: MessageSquare,
  order_placed: Package, order_fulfilled: Package, order_completed: Package, order_cancelled: Package,
  escrow_funded: Wallet, milestone_released: Wallet,
  review_received: Star,
  verification_approved: ShieldCheck, verification_rejected: ShieldCheck,
  dispute_opened: ShieldAlert, dispute_responded: ShieldAlert, dispute_resolved: ShieldAlert,
  company_invite: Building2, company_joined: Building2,
};

// Guard against notifications that point at routes the SPA doesn't have,
// which would otherwise land the user on the 404 page.
const KNOWN_ROUTES = new Set([
  "", "marketplace", "p", "c", "s", "j", "project", "rfq", "item", "u", "company",
  "admin", "post", "how-it-works", "legal", "support", "earnings", "signup", "login",
  "forgot-password", "reset-password", "dashboard", "messages", "wallet", "settings",
  "saved", "search", "verify", "get-verified", "worker-profile",
]);
function safeLink(link: unknown): string {
  if (typeof link !== "string" || !link.startsWith("/")) return "/dashboard";
  const first = link.split(/[/?#]/).filter(Boolean)[0] || "";
  return KNOWN_ROUTES.has(first) ? link : "/dashboard";
}

function ago(iso: string): string {
  const then = Date.parse(iso);
  if (!then) return "";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return `${Math.floor(d / 7)}w ago`;
}

export function NotificationBell() {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Lazy imports of api to keep this component light on first paint
  async function loadCount() {
    try { setUnread(await api.notifUnreadCount()); } catch { /* ignore */ }
  }
  async function loadList() {
    setBusy(true);
    try { const r = await api.getNotifications(); setItems(r?.notifications ?? []); setUnread(r?.unread ?? 0); }
    catch { setItems([]); }
    finally { setBusy(false); }
  }

  // Poll unread count; also refresh on route change.
  const [loc] = useLocation();
  useEffect(() => { loadCount(); }, [loc]);
  useEffect(() => {
    const h = setInterval(loadCount, 45000);
    return () => clearInterval(h);
  }, []);

  // Close on outside click
  useEffect(() => {
    function onDoc(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function toggle() { const next = !open; setOpen(next); if (next) loadList(); }

  async function openItem(n: any) {
    setOpen(false);
    if (!n.read) {
      try { const r = await api.markNotifRead(n.id); setUnread(r?.unread ?? Math.max(0, unread - 1)); } catch { /* ignore */ }
    }
    navigate(safeLink(n.link));
  }
  async function markAll() {
    try { await api.markAllNotifRead(); setUnread(0); setItems((prev) => (prev ? prev.map((x) => ({ ...x, read: true })) : prev)); } catch { /* ignore */ }
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="btn-ghost h-9 w-9 p-0 relative" title="Notifications" aria-label="Notifications">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-brand-red text-white text-[10px] font-bold leading-none flex items-center justify-center tabular-nums">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 card p-0 overflow-hidden shadow-lift z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <span className="font-bold text-navy">Notifications</span>
            {unread > 0 && <button onClick={markAll} className="text-xs text-brand-red font-semibold inline-flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" />Mark all read</button>}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {busy && !items ? (
              <div className="py-10 text-center text-mut"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : !items || items.length === 0 ? (
              <div className="py-10 text-center text-mut text-sm">
                <Bell className="w-7 h-7 mx-auto mb-2 opacity-40" />
                You're all caught up.
              </div>
            ) : (
              items.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <button key={n.id} onClick={() => openItem(n)}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-line hover:bg-navy-50 ${n.read ? "" : "bg-brand-red/5"}`}>
                    <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${n.read ? "bg-navy-50 text-navy-600" : "bg-brand-red/10 text-brand-red"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-navy truncate">{n.title}</span>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-brand-red shrink-0" />}
                      </div>
                      {n.body && <p className="text-xs text-mut mt-0.5 line-clamp-2">{n.body}</p>}
                      <span className="text-[11px] text-mut">{ago(n.createdAt)}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
